/* global Word console */

/**
 * Where an Assembly was sourced from. A "playbook" assembly is configured
 * against the Playbook Marketplace API; a "flow" assembly is configured
 * against the Projects API.
 */
export type AssemblySource = "playbook" | "flow";

export interface JyloDocumentResult {
  isJylo: boolean;
  /** Which backend the assembly is sourced from. Undefined when not a Jylo doc. */
  source?: AssemblySource;
  assemblyObjectIdentifier?: string;
  /** Set when source === "playbook". */
  playbookObjectIdentifier?: string;
  /** Set when source === "flow". */
  projectObjectIdentifier?: string;
  /** Set when source === "flow". */
  flowObjectIdentifier?: string;
}

function extractTag(xml: string, tag: string): string | undefined {
  const escapedTag = tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = xml.match(new RegExp(`<${escapedTag}>([\\s\\S]*?)</${escapedTag}>`));
  return match?.[1];
}

export class JyloDocumentDetector {
  /**
   * Detects whether the current Word document carries Jylo assembly metadata.
   *
   * Playbook-sourced assemblies embed a <PlaybookMetadata> custom XML part;
   * Flow-sourced assemblies embed a <FlowMetadata> part (written by the
   * Projects API when the assembly is created). The returned result tells the
   * UI which backend to talk to and supplies the identifiers needed to address
   * it.
   */
  static async isJyloDocument(): Promise<JyloDocumentResult> {
    try {
      return await Word.run(async (context) => {
        const customXmlParts = context.document.customXmlParts;
        context.load(customXmlParts, "items");
        await context.sync();

        for (let i = 0; i < customXmlParts.items.length; i++) {
          const xmlPart = customXmlParts.items[i];
          context.load(xmlPart, "xml");
          await context.sync();

          const xmlContent = xmlPart.xml;

          // Playbook-sourced assembly
          if (xmlContent.includes("<PlaybookMetadata>")) {
            const playbookObjectIdentifier = extractTag(xmlContent, "PlaybookObjectIdentifier");
            const assemblyObjectIdentifier = extractTag(xmlContent, "AssemblyObjectIdentifier");

            if (playbookObjectIdentifier && assemblyObjectIdentifier) {
              return {
                isJylo: true,
                source: "playbook",
                playbookObjectIdentifier,
                assemblyObjectIdentifier,
              };
            }

            return { isJylo: true };
          }

          // Flow-sourced assembly
          if (xmlContent.includes("<FlowMetadata>")) {
            const projectObjectIdentifier = extractTag(xmlContent, "ProjectObjectIdentifier");
            const flowObjectIdentifier = extractTag(xmlContent, "FlowObjectIdentifier");
            const assemblyObjectIdentifier = extractTag(xmlContent, "AssemblyObjectIdentifier");

            if (projectObjectIdentifier && flowObjectIdentifier && assemblyObjectIdentifier) {
              return {
                isJylo: true,
                source: "flow",
                projectObjectIdentifier,
                flowObjectIdentifier,
                assemblyObjectIdentifier,
              };
            }

            return { isJylo: true };
          }
        }

        return { isJylo: false };
      });
    } catch (error) {
      console.log("Error detecting Jylo document: " + error);
      return { isJylo: false };
    }
  }
}
